import React from "react";
import { Card, Form, Select, InputNumber, DatePicker, Space, Typography, Switch, Row, Col, Alert } from "antd";
import { CalendarOutlined, SyncOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { IRecurringSettings } from "../interfaces";

const { Text, Title } = Typography;
const { Option } = Select;

interface RecurringInvoiceSettingsProps {
  value?: IRecurringSettings;
  onChange?: (settings: IRecurringSettings) => void;
  disabled?: boolean;
}

export const RecurringInvoiceSettings: React.FC<RecurringInvoiceSettingsProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const handleChange = (field: keyof IRecurringSettings, newValue: any) => {
    if (!onChange) return;

    const updatedSettings: IRecurringSettings = {
      interval: "monthly",
      interval_count: 1,
      start_date: dayjs().format("YYYY-MM-DD"),
      current_occurrence: 0,
      next_invoice_date: dayjs().format("YYYY-MM-DD"),
      is_active: true,
      created_at: dayjs().toISOString(),
      ...value,
      [field]: newValue,
    };

    // Automatisch next_invoice_date berechnen
    if (field === "start_date" || field === "interval" || field === "interval_count") {
      const startDate = dayjs(updatedSettings.start_date);
      let nextDate = startDate;

      switch (updatedSettings.interval) {
        case "weekly":
          nextDate = startDate.add(updatedSettings.interval_count, "week");
          break;
        case "monthly":
          nextDate = startDate.add(updatedSettings.interval_count, "month");
          break;
        case "quarterly":
          nextDate = startDate.add(updatedSettings.interval_count * 3, "month");
          break;
        case "yearly":
          nextDate = startDate.add(updatedSettings.interval_count, "year");
          break;
      }

      updatedSettings.next_invoice_date = nextDate.format("YYYY-MM-DD");
    }

    onChange(updatedSettings);
  };

  const getIntervalLabel = () => {
    if (!value) return "";
    
    const { interval, interval_count } = value;
    const count = interval_count || 1;
    
    switch (interval) {
      case "weekly":
        return count === 1 ? "wöchentlich" : `alle ${count} Wochen`;
      case "monthly":
        return count === 1 ? "monatlich" : `alle ${count} Monate`;
      case "quarterly":
        return count === 1 ? "vierteljährlich" : `alle ${count} Quartale`;
      case "yearly":
        return count === 1 ? "jährlich" : `alle ${count} Jahre`;
      default:
        return "";
    }
  };

  const getNextInvoiceDates = (count: number = 5) => {
    if (!value) return [];
    
    const startDate = dayjs(value.start_date);
    const dates = [];
    
    for (let i = 0; i < count; i++) {
      let nextDate = startDate;
      
      switch (value.interval) {
        case "weekly":
          nextDate = startDate.add(i * value.interval_count, "week");
          break;
        case "monthly":
          nextDate = startDate.add(i * value.interval_count, "month");
          break;
        case "quarterly":
          nextDate = startDate.add(i * value.interval_count * 3, "month");
          break;
        case "yearly":
          nextDate = startDate.add(i * value.interval_count, "year");
          break;
      }
      
      dates.push(nextDate.format("DD.MM.YYYY"));
    }
    
    return dates;
  };

  return (
    <Card
      title={
        <Space>
          <SyncOutlined />
          <span>Wiederkehrende Rechnung</span>
        </Space>
      }
      size="small"
    >
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Alert
            message="Wiederkehrende Rechnungen"
            description="Diese Rechnung wird automatisch in den angegebenen Intervallen erstellt. Perfekt für Langzeitmieter oder regelmäßige Services."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        </Col>

        <Col span={12}>
          <Form.Item label="Wiederholungsintervall">
            <Select
              value={value?.interval || "monthly"}
              onChange={(val) => handleChange("interval", val)}
              disabled={disabled}
            >
              <Option value="weekly">Wöchentlich</Option>
              <Option value="monthly">Monatlich</Option>
              <Option value="quarterly">Vierteljährlich</Option>
              <Option value="yearly">Jährlich</Option>
            </Select>
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item label="Alle X Intervalle">
            <InputNumber
              min={1}
              max={12}
              value={value?.interval_count || 1}
              onChange={(val) => handleChange("interval_count", val || 1)}
              disabled={disabled}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item label="Startdatum">
            <DatePicker
              value={value?.start_date ? dayjs(value.start_date) : dayjs()}
              onChange={(date) => handleChange("start_date", date?.format("YYYY-MM-DD"))}
              disabled={disabled}
              style={{ width: "100%" }}
              format="DD.MM.YYYY"
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item label="Enddatum (optional)">
            <DatePicker
              value={value?.end_date ? dayjs(value.end_date) : null}
              onChange={(date) => handleChange("end_date", date?.format("YYYY-MM-DD"))}
              disabled={disabled}
              style={{ width: "100%" }}
              format="DD.MM.YYYY"
              placeholder="Unbegrenzt"
              allowClear
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item label="Max. Wiederholungen (optional)">
            <InputNumber
              min={1}
              max={999}
              value={value?.max_occurrences}
              onChange={(val) => handleChange("max_occurrences", val)}
              disabled={disabled}
              style={{ width: "100%" }}
              placeholder="Unbegrenzt"
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item label="Aktiv">
            <Switch
              checked={value?.is_active !== false}
              onChange={(checked) => handleChange("is_active", checked)}
              disabled={disabled}
              checkedChildren="Ja"
              unCheckedChildren="Nein"
            />
          </Form.Item>
        </Col>

        {value && (
          <Col span={24}>
            <Card size="small" title="Vorschau der nächsten Rechnungen">
              <Text strong>Wiederholung: </Text>
              <Text>{getIntervalLabel()}</Text>
              <br />
              <Text strong>Nächste Rechnungsdaten: </Text>
              <br />
              {getNextInvoiceDates().map((date, index) => (
                <Text key={index} style={{ display: "block", marginLeft: 16 }}>
                  {index + 1}. {date}
                </Text>
              ))}
            </Card>
          </Col>
        )}
      </Row>
    </Card>
  );
}; 